<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchPublicMarket,
  createPublicMarket,
  updatePublicMarket,
} from '@apis/admin/market'
import { fetchAssets } from '@apis/admin/asset'

export default {
  props: {
    market_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      assets: [],
      loading: false,
      active_tab: 'info',
    }
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([this.loadMarket(), this.loadAssets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadAssets() {
      if (this.account_seq) return []

      const { data } = await fetchAssets()

      this.assets = data
    },
    async loadMarket() {
      const { market_info } = await fetchPublicMarket(this.market_seq)
      this.params = defaults(market_info, {
        seq: null,
        title: null,
        code: '',
        maker_fee_rate: '0',
        taker_fee_rate: '0',
        target_asset_seq: null,
        source_asset_seq: null,
        is_active: 'Y',
        allow_deposit: 'Y',
        allow_withdraw: 'Y',
        use_min_call_amount: 'N',
        min_call_amount: 0,
        list_order: 0,
        regdate: null,
      })
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq
        ? updatePublicMarket
        : createPublicMarket
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('public_market_info.msg_modify')
        : this.$t('public_market_info.msg_registered')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    date,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('public_market_info.market_name') }}</div></th
              >
              <td colspan="3">
                <input
                  v-model.number="params.title"
                  type="text"
                  :class="[$style.inputTypeText, $style.fullWidth]"
                />
              </td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('public_market_info.market_seq') }}</div></th
              >
              <td>{{ params.seq || $t('admin.auto') }}</td>
              <th scope="row"
                ><div>{{ $t('admin.c2c_market_regdate') }}</div></th
              >
              <td>{{
                params.seq
                  ? date('Y-m-d H:i:s', new Date(params.regdate))
                  : $t('admin.before_enroll')
              }}</td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('public_market_info.code') }}</div></th
              >
              <td>
                <input
                  v-model.number="params.code"
                  type="text"
                  :class="[$style.inputTypeText, $style.w100]"
                />
              </td>
              <th scope="row"
                ><div>{{ $t('admin.modal_is_active') }}</div></th
              >
              <td
                ><ElSwitch
                  v-model="params.is_active"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('public_market_info.allow_deposit') }}</div></th
              >
              <td
                ><ElSwitch
                  v-model="params.allow_deposit"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
              <th scope="row"
                ><div>{{ $t('public_market_info.allow_withdrawals') }}</div></th
              >
              <td
                ><ElSwitch
                  v-model="params.allow_withdraw"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('public_market_info.output_order') }}</div></th
              >
              <td>
                <input
                  v-model.number="params.list_order"
                  type="text"
                  :class="[$style.inputTypeText, $style.w80, $style.tRight]"
                />
              </td>
              <th scope="row"
                ><div
                  >{{ $t('public_market_info.minimum_asking') }} (<ElCheckbox
                    v-model="params.use_min_call_amount"
                    false-label="N"
                    true-label="Y"
                    >{{ $t('public_market_info.use') }}</ElCheckbox
                  >)</div
                ></th
              >
              <td
                ><input
                  v-model.number="params.min_call_amount"
                  type="text"
                  :disabled="params.use_min_call_amount !== 'Y'"
                  :class="[$style.inputTypeText, $style.w80, $style.tRight]"
              /></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('public_market_info.base_assets') }}</div></th
              >
              <td>
                <ElSelect
                  v-if="!params.seq"
                  v-model="params.source_asset_seq"
                  filterable
                  placeholder="Select"
                >
                  <ElOption
                    v-for="source_asset in assets"
                    :key="source_asset.seq"
                    :label="`${source_asset.title} (${source_asset.code})`"
                    :value="source_asset.seq"
                  >
                  </ElOption>
                </ElSelect>
                <template v-else>{{ params.source_asset_title }}</template>
              </td>
              <th scope="row"
                ><div>{{ $t('public_market_info.relative_assets') }}</div></th
              >
              <td>
                <ElSelect
                  v-if="!params.seq"
                  v-model="params.target_asset_seq"
                  filterable
                  placeholder="Select"
                >
                  <ElOption
                    v-for="target_asset in assets"
                    :key="target_asset.seq"
                    :label="`${target_asset.title} (${target_asset.code})`"
                    :value="target_asset.seq"
                  >
                  </ElOption>
                </ElSelect>
                <template v-else>{{ params.target_asset_title }}</template>
              </td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('public_market_info.creator') }}</div></th
              >
              <td>
                <input
                  v-model.number="params.maker_fee_rate"
                  type="text"
                  :class="[$style.inputTypeText, $style.w80, $style.tRight]"
                />
                %
              </td>
              <th scope="row"
                ><div>{{ $t('public_market_info.user') }}</div></th
              >
              <td>
                <input
                  v-model.number="params.taker_fee_rate"
                  type="text"
                  :class="[$style.inputTypeText, $style.w80, $style.tRight]"
                />
                %
              </td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
